import type { AppRouteHandlerFn } from "next-auth/lib/types";
import type { ReadonlyRequestCookies } from "next/dist/server/web/spec-extension/adapters/request-cookies";
import type { NextRequest, NextResponse } from "next/server";

import type { Session } from "../domain";

export type AppRouteHandlers = Record<"GET" | "POST", (req: NextRequest) => Promise<Response>>;

export type AfterAuthHandlers = (req: NextRequest, session?: Session) => Promise<NextResponse>;

export type AuthMiddlewareConfiguration = {
  /**
   * A route or a URL of sign in page (URL use in case multiple subdomain signIn)
   * For example: '/login' or 'https://authentication.pharaday.net/login'
   */
  signInURL: string;
  /**
   * A route or a URL that the authenticated user will be redirect if they access authentication routes
   * default: '/'
   */
  signInCallbackURL?: string;
  /**
   * A list of routes that should be accessible with/without authentication
   * For example: ['/foo', '/bar(.*)'] or [/^\/foo\/.*$/]
   */
  publicRoutes?: string[];
  /**
   * A list of routes that should only be accessible without authentication
   * For example: ['/foo', '/bar(.*)'] or [/^\/foo\/.*$/]
   */
  guestRoutes?: string[];
  /**
   * This callback run after the authorization finished.
   * Meaning, it will run after public route flow and after the authorization flow valid.
   * !IMPORTANT: This will not run if the authorization flow failed meaning the user will be redirect to signInCallbackURL
   */
  afterAuth?: AfterAuthHandlers;
};

export type Credentials =
  | {
      type: "with-password";
      email: string;
      password: string;
    }
  | {
      type: "with-magic-link";
      email: string;
      code: string;
    }
  | {
      type: "with-totp";
      pendingToken: string;
      challenge: string;
      code: string;
    }
  | {
      type: "with-email-validation";
      pendingToken: string;
      code: string;
    };

export abstract class AuthProvider {
  abstract getRouteHandlers(): AppRouteHandlers;
  abstract authMiddleware(configuration: AuthMiddlewareConfiguration): AppRouteHandlerFn;
  abstract getSignInCallbackUrl(cookie: ReadonlyRequestCookies): string | undefined;
  abstract signIn(credentials: Credentials): Promise<{ success: boolean; pendingToken?: string }>;
  abstract signOut(): Promise<{ success: boolean }>;
  abstract getSession(): Promise<Session | null>;
  abstract updateSession(session: Session): Promise<void>;
  abstract renewSession(): Promise<Session | null>;
}
